import { useEffect, useRef, useCallback } from 'react';
import { useWillStore } from '@/lib/store';
import { createClient } from '@/lib/supabase/client';
import { transformToSectioned } from '@/lib/transformers';

const DEBOUNCE_MS = 2000; // Save every 2 seconds after changes

// Helper to build update payload from will data
function buildUpdatePayload(data: any, currentStep: number, completedSteps: number[]) {
  const dataToSave = transformToSectioned(data);
  const payload: any = {
    will_data: dataToSave,
    current_step: currentStep,
    completed_steps: completedSteps,
    updated_at: new Date().toISOString(),
  };

  if (data.personalDetails?.fullName) {
    payload.full_name = data.personalDetails.fullName;
  }

  return payload;
}

export function useAutoSave() {
  const { data, currentStep, completedSteps, willId, isAuthenticated } = useWillStore();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>('');
  const lastStepRef = useRef<number>(currentStep);

  const saveToSupabase = useCallback(async () => {
    if (!willId || !isAuthenticated) return;

    const dataString = JSON.stringify({ data, currentStep, completedSteps });

    // Don't save if nothing changed
    if (dataString === lastSavedRef.current) return;

    try {
      const supabase = createClient();
      const payload = buildUpdatePayload(data, currentStep, completedSteps);

      const { error } = await supabase.from('wills').update(payload).eq('id', willId);

      if (error) {
        console.error('Auto-save error:', error);
      } else {
        lastSavedRef.current = dataString;
        console.log('Auto-saved to Supabase');
      }
    } catch (err) {
      console.error('Auto-save exception:', err);
    }
  }, [data, currentStep, completedSteps, willId, isAuthenticated]);

  useEffect(() => {
    if (!willId || !isAuthenticated) return;

    // Clear any pending save
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    // If step changed, save immediately. Otherwise debounce.
    const stepChanged = currentStep !== lastStepRef.current;
    if (stepChanged) {
      lastStepRef.current = currentStep;
      saveToSupabase();
    } else {
      timeoutRef.current = setTimeout(saveToSupabase, DEBOUNCE_MS);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [data, currentStep, completedSteps, willId, isAuthenticated, saveToSupabase]);

  // Save immediately on unmount
  useEffect(() => {
    return () => {
      const { willId: currentWillId, isAuthenticated: isAuth, data: storeData, currentStep: step, completedSteps: steps } = useWillStore.getState();

      if (!currentWillId || !isAuth) return;

      const supabase = createClient();
      const payload = buildUpdatePayload(storeData, step, steps);

      supabase
        .from('wills')
        .update(payload)
        .eq('id', currentWillId)
        .then(({ error }) => {
          if (error) console.error('Final save error:', error);
          else console.log('Final save successful');
        });
    };
  }, []);

  return { saveNow: saveToSupabase };
}

